// ADR-0129 / #0571 — la lógica de «Asignar a contratista», extraída del
// drawer que la tenía (AssignObservacionModal) para que el panel de
// gestión del detalle la monte inline (README §3, cuerpo de «Aceptada»).
//
// La asignación ES la clasificación de especialidad (spec §3) cuando la
// receta declara taxonomía (`recipe.observationTaxonomy`, postventa) y la
// fila todavía no la tiene. Dos llamadas en secuencia en ese caso:
// POST /findings/:id/classification (persiste el par; el servidor
// registra sugerencia vs. final y re-deriva la criticidad) y después
// POST /findings/:id/assign. Con la especialidad resuelta —o sin
// taxonomía en la receta (facility, ADR-0134)— sólo corre /assign.
//
// El destino del estado no viaja: `/assign` mueve el estado, escribe el
// proveedor y emite el enlace en una sola transacción, y deriva el
// destino del grafo del lado del servidor. Una reasignación (autoarista
// asignada → asignada) revoca el enlace saliente ahí mismo. El proveedor
// va por id o se da de alta en línea (`newProvider`, ADR-0134 / #0431).
//
// Las opciones de especialidad/partida vienen del catálogo versionado,
// importado en build por ESTA superficie de supervisor; el catálogo no
// viaja en la receta, así que la PWA del propietario no ve códigos
// (invariante 8 del spec). Sólo se CONSULTA cuando la receta declara
// taxonomía: una receta sin ella (facility) no clasifica, no ordena la
// nómina por especialidad y no lee el catálogo. El módulo sigue
// importándose estático: las dos pantallas de postventa que lo usan
// (ObservacionesDelProyectoRoute, ClassifyFindingModal) ya lo traen al
// bundle, así que un import dinámico acá no separaría el chunk y sólo
// sumaría un estado de carga.
//
// La lista de especialidades se ORDENA por la matriz de combinaciones
// esperadas cuando hay tipo de falla — clave de orden, nunca filtro
// (invariante 7).

import { useMemo, useState } from "react";
import { taxonomyRegistry, TAXONOMY_VERSION_CURRENT } from "@bitacorapro/postventa-taxonomy";
import type { FindingAssignBody, FindingVisitSchedule } from "@bitacorapro/api-types";
import { useAssignFinding, useClassifyFinding, useProviders, useRecipe } from "../api/queries";

export type AssignFlowInput = {
  findingId: string;
  taxonomyVersion: string | null;
  failureTypeCode: string | null;
  currentSpecialtyCode: string | null;
  currentItemCode: string | null;
  aiSuggestedSpecialtyCode: string | null;
  aiSuggestedItemCode: string | null;
};

export type AssignResult = {
  url: string;
  revoked: boolean;
  /** Nombre corto del contratista elegido. */
  quien: string;
  /** false cuando el contratista no tiene correo: el enlace sólo existe
   *  en esta pantalla. Un core-api anterior al campo se lee como enviado. */
  notified: boolean;
  /** Con alta en línea: si la fila se creó (false = se adjuntó a uno
   *  existente del mismo nombre). null cuando vino por id. */
  providerCreated: boolean | null;
};

/** A quién se asigna: uno de la nómina, o uno nuevo dado de alta en la
 *  misma transacción (`newProvider`). */
export type AssignTarget =
  | { providerId: string }
  | { newProvider: NonNullable<FindingAssignBody["newProvider"]> };

export function useAssignFlow(input: AssignFlowInput) {
  const { data: recipe } = useRecipe();
  const taxonomy = recipe?.observationTaxonomy ?? null;
  const catalog = useMemo(
    () =>
      taxonomy === null
        ? null
        : (taxonomyRegistry.get(input.taxonomyVersion ?? taxonomy.version) ??
          taxonomyRegistry.get(TAXONOMY_VERSION_CURRENT) ??
          null),
    [taxonomy, input.taxonomyVersion]
  );

  // El paso de clasificación existe sólo con catálogo y mientras la
  // especialidad no está resuelta en la fila: un par resuelto no se
  // vuelve a preguntar.
  const needsClassification =
    catalog !== null && (input.currentSpecialtyCode === null || input.currentItemCode === null);

  const [specialty, setSpecialtyRaw] = useState<string>(
    input.currentSpecialtyCode ?? input.aiSuggestedSpecialtyCode ?? ""
  );
  const [item, setItem] = useState<string>(
    input.currentItemCode ?? input.aiSuggestedItemCode ?? ""
  );

  const classify = useClassifyFinding();
  const assign = useAssignFinding();
  const providers = useProviders();
  const pending = classify.isPending || assign.isPending;

  const especialidades = useMemo(() => {
    if (!catalog) return [];
    const list = [...catalog.especialidades];
    const tf = input.failureTypeCode;
    if (!tf) return list;
    const expected = (code: string) => (catalog.matrizEsperada[code]?.includes(tf) ? 0 : 1);
    return list.sort((a, b) => expected(a.codigo) - expected(b.codigo));
  }, [catalog, input.failureTypeCode]);

  const partidas = useMemo(
    () => catalog?.especialidades.find((e) => e.codigo === specialty)?.partidas ?? [],
    [catalog, specialty]
  );

  // Los contratistas cuya especialidad calza con la elegida van primero
  // — sugerencia, nunca filtro: la nómina entera queda seleccionable.
  const especialidadNombre = useMemo(
    () => catalog?.especialidades.find((e) => e.codigo === specialty)?.nombre ?? null,
    [catalog, specialty]
  );
  const nomina = useMemo(() => {
    const list = providers.data?.providers ?? [];
    if (!especialidadNombre) return list;
    const calza = (p: (typeof list)[number]) =>
      (p.specialty ?? "").toLowerCase().includes(especialidadNombre.toLowerCase());
    return [...list.filter(calza), ...list.filter((p) => !calza(p))];
  }, [providers.data, especialidadNombre]);

  const classificationReady = !needsClassification || (specialty !== "" && item !== "");

  function setSpecialty(code: string) {
    setSpecialtyRaw(code);
    setItem("");
  }

  async function submit(
    target: AssignTarget | string,
    note: string,
    opts: { plazoDias?: number; visit?: FindingVisitSchedule | undefined } = {}
  ): Promise<AssignResult> {
    const who: AssignTarget = typeof target === "string" ? { providerId: target } : target;
    if (needsClassification) {
      await classify.mutateAsync({
        id: input.findingId,
        body: { codEspecialidad: specialty, codPartida: item },
      });
    }
    const res = await assign.mutateAsync({
      id: input.findingId,
      body: {
        ...who,
        ...(note ? { note } : {}),
        ...(opts.plazoDias !== undefined ? { plazoDias: opts.plazoDias } : {}),
        // F8 — la visita, opcional, en el mismo acto.
        ...(opts.visit ? { visit: opts.visit } : {}),
      },
    });
    const elegido =
      "providerId" in who ? nomina.find((p) => p.id === who.providerId)?.shortName : undefined;
    return {
      url: res.url,
      revoked: res.revokedPreviousLink,
      quien:
        elegido ??
        res.providerShortName ??
        ("newProvider" in who ? who.newProvider.shortName : "el contratista"),
      notified: res.contractorNotified !== false,
      providerCreated: res.providerCreated ?? null,
    };
  }

  return {
    needsClassification,
    specialty,
    setSpecialty,
    item,
    setItem,
    especialidades,
    partidas,
    providers,
    nomina,
    classificationReady,
    pending,
    submit,
  };
}
